// ─────────────────────────────────────────────────────────────
import { supabase } from '@/lib/supabase';

// ZIFEK CORE — Catalogue des permissions granulaires
// Les rôles (core_roles) portent un ensemble de permissions
// (core_role_permissions). Ce catalogue est la source de vérité
// côté interface : groupes, libellés et icônes.
// ─────────────────────────────────────────────────────────────

export interface PermissionDef {
  key: string;
  label: string;
}

export interface PermissionGroup {
  key: string;
  label: string;
  icon: string;
  permissions: PermissionDef[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    key: 'dashboard',
    label: 'Tableau de bord',
    icon: 'ri-dashboard-line',
    permissions: [{ key: 'dashboard.view', label: 'Accéder au tableau de bord' }],
  },
  {
    key: 'products',
    label: 'Produits',
    icon: 'ri-shopping-bag-3-line',
    permissions: [
      { key: 'products.read', label: 'Consulter les produits' },
      { key: 'products.write', label: 'Créer et modifier les produits' },
    ],
  },
  {
    key: 'stock',
    label: 'Gestion de stock',
    icon: 'ri-archive-line',
    permissions: [
      { key: 'stock.read', label: 'Consulter et suivre le stock' },
      { key: 'stock.write', label: 'Ajuster le stock (entrées / sorties)' },
    ],
  },
  {
    key: 'services',
    label: 'Services',
    icon: 'ri-service-line',
    permissions: [
      { key: 'services.read', label: 'Consulter les services' },
      { key: 'services.write', label: 'Créer et modifier les services' },
    ],
  },
  {
    key: 'orders',
    label: 'Commandes',
    icon: 'ri-file-list-3-line',
    permissions: [
      { key: 'orders.read', label: 'Consulter les commandes' },
      { key: 'orders.write', label: 'Gérer les commandes' },
    ],
  },
  {
    key: 'customers',
    label: 'Clients',
    icon: 'ri-user-line',
    permissions: [
      { key: 'customers.read', label: 'Consulter les clients' },
      { key: 'customers.write', label: 'Gérer les clients' },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: 'ri-funds-line',
    permissions: [
      { key: 'finance.read', label: 'Consulter la finance' },
      { key: 'finance.write', label: 'Gérer la finance' },
    ],
  },
  {
    key: 'credit',
    label: 'Crédit',
    icon: 'ri-book-2-line',
    permissions: [
      { key: 'credit.read', label: 'Consulter le crédit' },
      { key: 'credit.write', label: 'Gérer le crédit' },
    ],
  },
  {
    key: 'content',
    label: 'Contenu du site',
    icon: 'ri-edit-line',
    permissions: [
      { key: 'content.read', label: 'Consulter le contenu' },
      { key: 'content.write', label: 'Modifier le contenu du site' },
    ],
  },
  {
    key: 'analytics',
    label: 'Statistiques',
    icon: 'ri-line-chart-line',
    permissions: [{ key: 'analytics.view', label: 'Voir les statistiques et rapports' }],
  },
  {
    key: 'members',
    label: 'Équipe & rôles',
    icon: 'ri-group-line',
    permissions: [
      { key: 'members.read', label: 'Consulter les membres' },
      { key: 'members.manage', label: 'Gérer les membres' },
      { key: 'roles.manage', label: 'Gérer les rôles et permissions' },
    ],
  },
  {
    key: 'locations',
    label: 'Établissements',
    icon: 'ri-store-2-line',
    permissions: [
      { key: 'locations.read', label: 'Consulter les établissements' },
      { key: 'locations.manage', label: 'Gérer les établissements' },
    ],
  },
  {
    key: 'settings',
    label: 'Paramètres',
    icon: 'ri-settings-3-line',
    permissions: [
      { key: 'settings.read', label: 'Consulter les paramètres' },
      { key: 'settings.write', label: 'Modifier les paramètres' },
    ],
  },
];

export const ALL_PERMISSIONS: string[] = PERMISSION_GROUPS.flatMap((g) =>
  g.permissions.map((p) => p.key),
);

export function permissionLabel(key: string): string {
  if (key === '*') return 'Accès complet';
  for (const g of PERMISSION_GROUPS) {
    const found = g.permissions.find((p) => p.key === key);
    if (found) return found.label;
  }
  return key;
}

/** Indique si un ensemble de permissions couvre une permission donnée. */
export function roleHasPermission(perms: string[], permission: string): boolean {
  return perms.includes('*') || perms.includes(permission);
}

/** Résumé court d'un ensemble de permissions (pour les chips). */
export function permissionSummary(perms: string[]): string {
  if (perms.includes('*')) return 'Toutes les permissions';
  return `${perms.length} permission(s)`;
}

export const SYSTEM_ROLE_META: Record<string, { label: string; icon: string; className: string }> = {
  owner: { label: 'Propriétaire', icon: 'ri-shield-star-line', className: 'bg-primary-100 text-primary-900' },
  admin: { label: 'Administrateur', icon: 'ri-shield-keyhole-line', className: 'bg-primary-100 text-primary-800' },
  manager: { label: 'Manager', icon: 'ri-user-star-line', className: 'bg-secondary-100 text-secondary-900' },
  accountant: { label: 'Comptable', icon: 'ri-calculator-line', className: 'bg-secondary-100 text-secondary-900' },
  receptionist: { label: 'Réceptionniste', icon: 'ri-customer-service-2-line', className: 'bg-accent-100 text-accent-900' },
  employee: { label: 'Employé', icon: 'ri-user-line', className: 'bg-background-100 text-foreground-700' },
  vendor: { label: 'Vendeur', icon: 'ri-store-3-line', className: 'bg-accent-100 text-accent-900' },
  support: { label: 'Support', icon: 'ri-question-answer-line', className: 'bg-background-100 text-foreground-700' },
};

export function roleMeta(roleKey: string, fallbackLabel?: string | null) {
  return (
    SYSTEM_ROLE_META[roleKey] || {
      label: fallbackLabel || roleKey,
      icon: 'ri-price-tag-3-line',
      className: 'bg-background-100 text-foreground-700',
    }
  );
}

// ─────────────────────────────────────────────────────────────
// AP-PLICATION DES PERMISSIONS AUX ROUTES DU DASHBOARD
// Chaque chemin du dashboard est associé à la (aux) permission(s)
// requise(s). Le masquage des menus et le blocage d'accès utilisent
// ces règles. Aucune règle = chemin autorisé (fail-open).
// ─────────────────────────────────────────────────────────────

export interface RoutePermissionRule {
  /** Préfixe de chemin (sans slash final). */
  prefix: string;
  /** Permission unique ou liste (au moins une requise). */
  permission: string | string[];
}

// Les préfixes les plus longs gagnent (voir requiredPermissionsForPath).
export const DASHBOARD_ROUTE_PERMISSIONS: RoutePermissionRule[] = [
  { prefix: '/dashboard', permission: 'dashboard.view' },
  { prefix: '/dashboard/products', permission: 'products.read' },
  { prefix: '/dashboard/gestion-stock', permission: 'stock.read' },
  { prefix: '/dashboard/caisse', permission: 'products.read' },
  { prefix: '/dashboard/services', permission: 'services.read' },
  { prefix: '/dashboard/orders', permission: 'orders.read' },
  { prefix: '/dashboard/customers-manager', permission: 'customers.read' },
  { prefix: '/dashboard/customers', permission: 'customers.read' },
  { prefix: '/dashboard/messages', permission: 'customers.read' },
  { prefix: '/dashboard/credit', permission: 'credit.read' },
  { prefix: '/dashboard/finance', permission: 'finance.read' },
  { prefix: '/dashboard/wallet', permission: 'finance.read' },
  { prefix: '/dashboard/payments', permission: 'settings.read' },
  { prefix: '/dashboard/store', permission: 'settings.read' },
  { prefix: '/dashboard/core', permission: ['members.read', 'locations.read'] },
  { prefix: '/dashboard/users', permission: ['members.read', 'members.manage', 'roles.manage'] },
  { prefix: '/dashboard/journal', permission: ['members.read', 'members.manage', 'roles.manage'] },
  { prefix: '/dashboard/site-content', permission: 'content.read' },
  { prefix: '/dashboard/home-blocks', permission: 'content.write' },
  { prefix: '/dashboard/banners', permission: 'content.write' },
  { prefix: '/dashboard/custom-pages', permission: 'content.write' },
  { prefix: '/dashboard/media', permission: 'content.read' },
  { prefix: '/dashboard/documents', permission: 'content.read' },
  { prefix: '/dashboard/storage', permission: 'settings.read' },
  { prefix: '/dashboard/analytics-advanced', permission: 'analytics.view' },
  { prefix: '/dashboard/analytics-pro', permission: 'analytics.view' },
  { prefix: '/dashboard/analytics', permission: 'analytics.view' },
  { prefix: '/dashboard/appstore', permission: 'settings.read' },
  { prefix: '/dashboard/themestore', permission: 'settings.write' },
  { prefix: '/dashboard/settings', permission: 'settings.read' },
  { prefix: '/dashboard/restaurant', permission: 'products.read' },
];

/**
 * Renvoie la (les) permission(s) requise(s) pour un chemin de dashboard,
 * ou null si le chemin n'est soumis à aucune règle (autorisé par défaut).
 */
export function requiredPermissionsForPath(pathname: string): string | string[] | null {
  const path = (pathname.split('?')[0] || '/').replace(/\/+$/, '') || '/';
  let best: RoutePermissionRule | null = null;
  for (const rule of DASHBOARD_ROUTE_PERMISSIONS) {
    if (path === rule.prefix || path.startsWith(`${rule.prefix}/`)) {
      if (!best || rule.prefix.length > best.prefix.length) best = rule;
    }
  }
  return best ? best.permission : null;
}

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 36. PERMISSIONS  &  37. RESOURCE-LEVEL SECURITY
// Permissions granulaires (customer.read, invoice.create, …) pouvant
// être globales, par entreprise, par établissement, par module ou par
// ressource. La sécurité ne se limite pas à un rôle : on vérifie
//   User → Business → Branch → Resource.
// ─────────────────────────────────────────────────────────────

export const PERMISSION_MODULES: { key: string; label: string; icon: string }[] = [
  { key: 'customer', label: 'Clients', icon: 'ri-user-star-line' },
  { key: 'appointment', label: 'Rendez-vous', icon: 'ri-calendar-line' },
  { key: 'invoice', label: 'Factures', icon: 'ri-file-list-3-line' },
  { key: 'payment', label: 'Paiements', icon: 'ri-money-dollar-circle-line' },
  { key: 'product', label: 'Produits', icon: 'ri-shopping-bag-line' },
  { key: 'service', label: 'Services', icon: 'ri-service-line' },
  { key: 'order', label: 'Commandes', icon: 'ri-shopping-cart-line' },
  { key: 'business', label: 'Entreprise', icon: 'ri-building-2-line' },
];

export function permissionModuleMeta(key: string | null | undefined) {
  return PERMISSION_MODULES.find((m) => m.key === key) || { key: 'other', label: 'Autre', icon: 'ri-shield-keyhole-line' };
}

/** Périmètres d'application d'une permission. */
export const PERMISSION_SCOPES: { key: string; label: string; desc: string; icon: string }[] = [
  { key: 'global', label: 'Globale', desc: 'S’applique à toute l’entreprise', icon: 'ri-global-line' },
  { key: 'company', label: 'Entreprise', desc: 'Limitée à une entreprise', icon: 'ri-building-2-line' },
  { key: 'branch', label: 'Établissement', desc: 'Limitée à un établissement', icon: 'ri-map-pin-line' },
  { key: 'module', label: 'Module', desc: 'Limitée à un module', icon: 'ri-apps-2-line' },
  { key: 'resource', label: 'Ressource', desc: 'Limitée à une ressource précise', icon: 'ri-focus-3-line' },
];

export function permissionScopeMeta(key: string | null | undefined) {
  return PERMISSION_SCOPES.find((s) => s.key === key) || PERMISSION_SCOPES[0];
}

export interface CorePermission {
  id: number;
  key: string;
  module: string | null;
  resource: string | null;
  action: string | null;
  label: string | null;
  description: string | null;
}

export async function listCorePermissions(): Promise<CorePermission[]> {
  const { data, error } = await supabase
    .from('core_permissions')
    .select('*')
    .order('module', { ascending: true })
    .order('key', { ascending: true });
  if (error) throw error;
  return (data || []) as CorePermission[];
}

export async function createCorePermission(input: {
  key: string;
  module: string;
  resource: string;
  action: string;
  label: string;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_permissions')
    .insert({
      key: input.key,
      module: input.module,
      resource: input.resource,
      action: input.action,
      label: input.label,
      description: null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function deleteCorePermission(id: number): Promise<void> {
  const { error } = await supabase.from('core_permissions').delete().eq('id', id);
  if (error) throw error;
}

// ── 37. Resource-level security ──

export interface CoreAccessScope {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  user_id: number;
  membership_id: number | null;
  location_id: number | null;
  module: string | null;
  resource_type: string | null;
  resource_id: string | null;
  can_read: boolean | null;
  can_write: boolean | null;
  can_delete: boolean | null;
  created_at: string | null;
}

export async function listAccessScopes(organizationId: number): Promise<CoreAccessScope[]> {
  const { data, error } = await supabase
    .from('core_access_scopes')
    .select('*')
    .eq('organization_id', organizationId)
    .order('id', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreAccessScope[];
}

export interface AccessScopeInput {
  tenantId: number | null;
  organizationId: number;
  userId: number;
  locationId?: number | null;
  module?: string | null;
  resourceType?: string | null;
  resourceId?: string | null;
  canRead?: boolean;
  canWrite?: boolean;
  canDelete?: boolean;
}

export async function createAccessScope(input: AccessScopeInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_access_scopes')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      user_id: input.userId,
      location_id: input.locationId ?? null,
      module: input.module || null,
      resource_type: input.resourceType || null,
      resource_id: input.resourceId || null,
      can_read: input.canRead ?? true,
      can_write: input.canWrite ?? false,
      can_delete: input.canDelete ?? false,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function deleteAccessScope(id: number): Promise<void> {
  const { error } = await supabase.from('core_access_scopes').delete().eq('id', id);
  if (error) throw error;
}

export interface AccessCheckContext {
  userId: number;
  organizationId: number;
  locationId?: number | null;
  resourceType?: string | null;
  resourceId?: string | null;
  action?: 'read' | 'write' | 'delete';
}

/**
 * Vérifie l'accès d'un utilisateur à une ressource :
 * User → Business → Branch → Resource. Renvoie true si un périmètre
 * correspondant autorise l'action demandée.
 */
export async function checkResourceAccess(ctx: AccessCheckContext): Promise<boolean> {
  const { data } = await supabase
    .from('core_access_scopes')
    .select('*')
    .eq('organization_id', ctx.organizationId)
    .eq('user_id', ctx.userId);
  const scopes = (data || []) as CoreAccessScope[];
  if (scopes.length === 0) return false;
  const action = ctx.action || 'read';
  return scopes.some((s) => {
    if (s.location_id != null && ctx.locationId != null && s.location_id !== ctx.locationId) return false;
    if (s.resource_type && ctx.resourceType && s.resource_type !== ctx.resourceType) return false;
    if (s.resource_id && ctx.resourceId && s.resource_id !== String(ctx.resourceId)) return false;
    if (action === 'read') return !!s.can_read;
    if (action === 'write') return !!s.can_write;
    return !!s.can_delete;
  });
}

export interface PermissionStats {
  total: number;
  byModule: Record<string, number>;
  totalScopes: number;
}

export async function fetchPermissionStats(organizationId: number): Promise<PermissionStats> {
  const [perms, scopes] = await Promise.all([listCorePermissions(), listAccessScopes(organizationId)]);
  const byModule: Record<string, number> = {};
  perms.forEach((p) => {
    const m = p.module || 'other';
    byModule[m] = (byModule[m] || 0) + 1;
  });
  return { total: perms.length, byModule, totalScopes: scopes.length };
}